/**
 * Shared interaction option state + wire payload for enum AI prompt forms.
 *
 * EMG-GQ-ENUM-CATALOG: option keys mirror Agent `question_type_config.py` /
 * `interactionCatalog.ts` (snake_case on the wire).
 */

import { useCallback, useEffect, useState } from 'react';
import find from 'lodash/find';
import { getInteractionEntry } from './interactionCatalog';
import type { InteractionOptionField } from './interactionCatalog';
import { getPromptLocale } from './i18n';
import { getQuestionTypeLabel } from './questionTypeLabels';
import type { QuestionTypeOption } from './types';

export function buildDefaultInteractionOptions(
    questionType: string,
): Record<string, string | number> {
    const entry = getInteractionEntry(questionType);
    if (!entry) return {};
    const values: Record<string, string | number> = {};
    for (const field of entry.optionFields) {
        values[field.key] = field.defaultValue;
    }
    return values;
}

export function useInteractionOptions(questionType: string) {
    const [values, setValues] = useState<Record<string, string | number>>(() =>
        buildDefaultInteractionOptions(questionType),
    );

    useEffect(() => {
        setValues(buildDefaultInteractionOptions(questionType));
    }, [questionType]);

    const updateField = useCallback((key: string, value: string | number) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    return { values, updateField };
}

/** Human label — never show raw enum slug when catalog knows the type. */
export function resolveQuestionTypeDisplayName(
    questionType: string,
    options?: QuestionTypeOption[],
    locale?: string,
): string {
    const lang = locale ?? getPromptLocale();
    const catalogLabel = getQuestionTypeLabel(questionType, lang);
    if (catalogLabel !== questionType) {
        return catalogLabel;
    }
    const match = find(
        options ?? [],
        (o) => o.slug === questionType || o.code === questionType,
    );
    return match?.name ?? catalogLabel;
}

export function getInteractionOptionFields(questionType: string): InteractionOptionField[] {
    return getInteractionEntry(questionType)?.optionFields ?? [];
}
